import React, { useState, useEffect } from 'react';
import './Discounts.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Discounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [form, setForm] = useState({
    code: '', description: '', discount_type: 'percentage', discount_value: '',
    min_order_amount: '', max_discount_amount: '', usage_limit: '',
    starts_at: '', expires_at: '', is_active: true, applies_to: 'all', applies_to_value: ''
  });

  const fetchDiscounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/discounts`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setDiscounts(data.data);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDiscounts(); }, []);

  const resetForm = () => {
    setForm({
      code: '', description: '', discount_type: 'percentage', discount_value: '',
      min_order_amount: '', max_discount_amount: '', usage_limit: '',
      starts_at: '', expires_at: '', is_active: true, applies_to: 'all', applies_to_value: ''
    });
    setEditingDiscount(null);
    setShowForm(false);
  };

  const handleEdit = (d) => {
    setForm({
      code: d.code, description: d.description || '', discount_type: d.discount_type,
      discount_value: d.discount_value, min_order_amount: d.min_order_amount || '',
      max_discount_amount: d.max_discount_amount || '', usage_limit: d.usage_limit || '',
      starts_at: d.starts_at ? d.starts_at.slice(0, 16) : '',
      expires_at: d.expires_at ? d.expires_at.slice(0, 16) : '',
      is_active: d.is_active, applies_to: d.applies_to || 'all',
      applies_to_value: d.applies_to_value || ''
    });
    setEditingDiscount(d);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discount_value) return alert('Code and value are required');
    const token = localStorage.getItem('token');
    const url = editingDiscount ? `${API}/discounts/${editingDiscount.id}` : `${API}/discounts`;
    const method = editingDiscount ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          discount_value: parseFloat(form.discount_value),
          min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : 0,
          max_discount_amount: form.max_discount_amount ? parseFloat(form.max_discount_amount) : null,
          usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
          starts_at: form.starts_at || null, expires_at: form.expires_at || null
        })
      });
      const data = await res.json();
      if (data.success) { resetForm(); fetchDiscounts(); }
      else alert(data.error || 'Failed to save');
    } catch (err) { console.error('Error:', err); alert('Failed to save discount'); }
  };

  const handleToggle = async (id) => {
    const token = localStorage.getItem('token');
    await fetch(`${API}/discounts/${id}/toggle`, {
      method: 'PATCH', headers: { Authorization: `Bearer ${token}` }
    });
    fetchDiscounts();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount code?')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API}/discounts/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    fetchDiscounts();
  };

  const getStatus = (d) => {
    if (!d.is_active) return { label: 'Inactive', cls: 'status-inactive' };
    const now = new Date();
    if (d.expires_at && new Date(d.expires_at) < now) return { label: 'Expired', cls: 'status-expired' };
    if (d.starts_at && new Date(d.starts_at) > now) return { label: 'Scheduled', cls: 'status-scheduled' };
    if (d.usage_limit && d.usage_count >= d.usage_limit) return { label: 'Used Up', cls: 'status-expired' };
    return { label: 'Active', cls: 'status-active' };
  };

  if (loading) return <div className="discounts-loading">Loading discounts...</div>;

  return (
    <div className="discounts-page">
      <div className="discounts-header">
        <div>
          <h2>Discount Codes</h2>
          <p className="discounts-subtitle">{discounts.length} discount code{discounts.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-create-discount" onClick={() => { resetForm(); setShowForm(true); }}>
          + Create Discount
        </button>
      </div>

      {showForm && (
        <div className="discount-form-overlay" onClick={(e) => e.target === e.currentTarget && resetForm()}>
          <div className="discount-form-modal">
            <div className="form-modal-header">
              <h3>{editingDiscount ? 'Edit Discount' : 'Create Discount'}</h3>
              <button className="btn-close-modal" onClick={resetForm}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="discount-form">
              <div className="form-row-2">
                <div className="form-field">
                  <label>Code</label>
                  <input type="text" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}
                    placeholder="e.g. SAVE10" required />
                </div>
                <div className="form-field">
                  <label>Type</label>
                  <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-field">
                  <label>Value {form.discount_type === 'percentage' ? '(%)' : '($)'}</label>
                  <input type="number" step="0.01" value={form.discount_value}
                    onChange={e => setForm({...form, discount_value: e.target.value})} required />
                </div>
                <div className="form-field">
                  <label>Min Order Amount ($)</label>
                  <input type="number" step="0.01" value={form.min_order_amount}
                    onChange={e => setForm({...form, min_order_amount: e.target.value})} placeholder="0" />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-field">
                  {form.discount_type === 'percentage' && (
                    <><label>Max Discount ($)</label>
                    <input type="number" step="0.01" value={form.max_discount_amount}
                      onChange={e => setForm({...form, max_discount_amount: e.target.value})} placeholder="No cap" /></>
                  )}
                  {form.discount_type !== 'percentage' && (
                    <><label>Max Discount</label><input disabled placeholder="N/A for fixed" /></>
                  )}
                </div>
                <div className="form-field">
                  <label>Usage Limit</label>
                  <input type="number" value={form.usage_limit}
                    onChange={e => setForm({...form, usage_limit: e.target.value})} placeholder="Unlimited" />
                </div>
              </div>
              <div className="form-field">
                <label>Description</label>
                <input type="text" value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})} placeholder="Internal note" />
              </div>
              <div className="form-row-2">
                <div className="form-field">
                  <label>Starts At</label>
                  <input type="datetime-local" value={form.starts_at}
                    onChange={e => setForm({...form, starts_at: e.target.value})} />
                </div>
                <div className="form-field">
                  <label>Expires At</label>
                  <input type="datetime-local" value={form.expires_at}
                    onChange={e => setForm({...form, expires_at: e.target.value})} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn-save-discount">
                  {editingDiscount ? 'Update' : 'Create'} Discount
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {discounts.length === 0 ? (
        <div className="discounts-empty">
          <p>No discount codes yet</p>
          <p>Create your first discount code to offer deals to customers.</p>
        </div>
      ) : (
        <div className="discounts-table-wrapper">
          <table className="discounts-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Min Order</th>
                <th>Usage</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map(d => {
                const status = getStatus(d);
                return (
                  <tr key={d.id}>
                    <td><span className="discount-code-badge">{d.code}</span>
                      {d.description && <span className="discount-desc">{d.description}</span>}
                    </td>
                    <td>{d.discount_type === 'percentage' ? `${d.discount_value}%` : `$${Number(d.discount_value).toFixed(2)}`}
                      {d.max_discount_amount && <span className="discount-cap"> (max ${Number(d.max_discount_amount).toFixed(2)})</span>}
                    </td>
                    <td>{d.min_order_amount > 0 ? `$${Number(d.min_order_amount).toFixed(2)}` : '—'}</td>
                    <td>{d.usage_count}{d.usage_limit ? ` / ${d.usage_limit}` : ' / ∞'}</td>
                    <td className="discount-dates">
                      {d.starts_at ? new Date(d.starts_at).toLocaleDateString() : 'Now'}
                      {' → '}
                      {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td><span className={`status-badge ${status.cls}`}>{status.label}</span></td>
                    <td className="discount-actions">
                      <button className="btn-action btn-edit" onClick={() => handleEdit(d)}>Edit</button>
                      <button className="btn-action btn-toggle" onClick={() => handleToggle(d.id)}>
                        {d.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="btn-action btn-delete" onClick={() => handleDelete(d.id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Discounts;
